import React from 'react';
import { useTranslation } from 'react-i18next';

import { LineChart, Skeleton, DayBars, type ChartSeries, type DayBarSeries } from '../../../../components';
import { HOME_SERIES_COLORS } from '../../../../theme/tokens';
import type { DayPair, HomeData, HomePeriod } from '../../../../mock-data/types';
import { visitorsOnBuckets } from '../../../website-analytics/queries';
import type { WebsiteVisitors } from '../../../website-analytics/useWebsiteVisitors';
import { MetricFrame } from '../MetricFrame';
import { Stat } from '../Stat';
import { Legend } from '../Legend';
import { Inner, StatsRow, VDivider, ChartCol, LegendWrap, StatLink } from './AcquisitionMetric.styles';

// Mirrors the LineChart `height` so the placeholder reserves the same band.
const CHART_HEIGHT = 120;

/** Compact count for the day-bar value readouts ("80", "1.2k"). */
const countFmt = (n: number): string => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : `${Math.round(n)}`);

export type AcquisitionMetricProps = {
  data: HomeData['acquisition'];
  /** Delta caption ("vs yesterday" / "vs last week" / …) — adapts to the range. */
  comparison: string;
  /** The selected range — drives whether the trend chart or the day-comparison bars
   *  show (Today/Yesterday have no usable hourly slices). */
  interval: HomePeriod;
  /** While true, the figures + trend chart show placeholders (the endpoint is loading). */
  loading?: boolean;
  /** The brand's website visitors (all of them, its site and landing pages) beside the sign-ups, from the analytics
   *  service; absent where that service is not shown (the figure is left out). `error`: it could not be read.
   *  `points`: visitors on each chart point, drawn as a faint backdrop on its own scale; `day`: the Today / Yesterday
   *  bars' pair, a row on its own scale. Either absent: not shown. `people`: they count people (named so);
   *  `indeterminate`: there is nothing to count (a dash); `caption`: said in place of the comparison, with no delta. */
  visitors?: {
    value: number;
    delta: number;
    loading: boolean;
    error: boolean;
    points?: number[];
    day?: DayPair | null;
    people?: boolean;
    indeterminate?: boolean;
    caption?: string;
  };
  /** Opens the visitors in Stats → Website: the whole visitors stat becomes a button. */
  onOpenVisitors?: () => void;
  /** Opens the sign-ups in Stats: the Registrations and FTDs stats become buttons. */
  onOpenSignups?: () => void;
};

/**
 * The visitors stat for the brand's website visitors as read (undefined: left out, while unavailable or not read).
 * `caption`: the translated text of `visitors.caption`; `bucketStarts`: the chart's points, where Home charts by the
 * day. Pure.
 */
export const websiteVisitorsFigure = (
  visitors: WebsiteVisitors,
  opts: { people: boolean; caption?: string; bucketStarts?: number[] },
): AcquisitionMetricProps['visitors'] => {
  if (visitors.status === 'unavailable' || visitors.status === 'idle') return undefined;
  const ready = visitors.status === 'ready';
  return {
    value: visitors.visitors,
    delta: visitors.delta,
    loading: visitors.status === 'loading',
    error: visitors.status === 'error',
    // Only on the daily charts: on the monthly ones the visitors per day are not read (they do not add up).
    points:
      ready && visitors.days && opts.bucketStarts ? visitorsOnBuckets(visitors.days, opts.bucketStarts) : undefined,
    day: ready ? visitors.day : null,
    people: opts.people,
    indeterminate: ready && visitors.homeState === 'no-default-host',
    caption: ready ? opts.caption : undefined,
  };
};

/**
 * "Acquisition" — registrations / FTDs split stats beside a two-series trend
 * chart. The series colours come from `HOME_SERIES_COLORS`; the legend mirrors
 * them. On the Today / Yesterday ranges the chart collapses to a prior-day vs
 * current-day {@link DayBars} comparison.
 */
export const AcquisitionMetric = ({
  data,
  comparison,
  interval,
  loading = false,
  visitors,
  onOpenVisitors,
  onOpenSignups,
}: AcquisitionMetricProps): React.ReactElement => {
  const { t } = useTranslation();
  // A stat as a button into its Stats view (as it is, without `onOpen`).
  const linked = (stat: React.ReactElement, name: string, onOpen?: () => void): React.ReactElement =>
    onOpen ? (
      <StatLink
        role="button"
        tabIndex={0}
        aria-label={t('Open {{name}}', { name })}
        onClick={onOpen}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen();
          }
        }}
      >
        {stat}
      </StatLink>
    ) : (
      stat
    );
  const dayView = interval === 'TODAY' || interval === 'YESTERDAY';
  const series: ChartSeries[] = [
    { name: t('Registrations'), color: HOME_SERIES_COLORS.registrations, points: data.chart.registrations },
    { name: t('FTDs'), color: HOME_SERIES_COLORS.ftds, points: data.chart.ftds },
  ];
  const visitorsName = visitors?.people ? t('People') : t('Website visitors');
  const visitorsDay = visitors?.day ?? null;
  const visitorsPoints = visitors?.points?.length ? visitors.points : undefined;
  const dayBars: DayBarSeries[] = [
    // The visitors lead (the funnel reads visitors → sign-ups), on their own scale so the sign-up rows keep theirs.
    ...(visitorsDay
      ? [{ name: visitorsName, color: HOME_SERIES_COLORS.visitors, ...visitorsDay, ownScale: true }]
      : []),
    { name: t('Registrations'), color: HOME_SERIES_COLORS.registrations, ...data.days.registrations },
    { name: t('FTDs'), color: HOME_SERIES_COLORS.ftds, ...data.days.ftds },
  ];
  const visitorsStat = visitors ? (
    <Stat
      label={visitorsName}
      value={visitors.value}
      delta={visitors.caption ? undefined : visitors.delta}
      caption={visitors.caption ?? comparison}
      loading={visitors.loading}
      error={visitors.error}
      indeterminate={visitors.indeterminate}
    />
  ) : null;

  return (
    <MetricFrame title={t('Acquisition')} sub={t('New players joining your website')}>
      <Inner $wide={visitors !== undefined}>
        <StatsRow>
          {/* The funnel reads left to right: the visitors first, then the sign-ups they turned into. */}
          {visitors ? (
            <>
              {visitorsStat ? linked(visitorsStat, visitorsName, onOpenVisitors) : null}
              <VDivider />
            </>
          ) : null}
          {linked(
            <Stat
              label={t('Registrations')}
              value={data.registrations.value}
              delay={visitors ? 120 : undefined}
              delta={data.registrations.delta}
              caption={comparison}
              loading={loading}
              error={data.registrations.error}
            />,
            t('Registrations'),
            onOpenSignups,
          )}
          <VDivider />
          {linked(
            <Stat
              label={t('FTDs')}
              value={data.ftds.value}
              delay={visitors ? 240 : 120}
              delta={data.ftds.delta}
              caption={comparison}
              loading={loading}
              error={data.ftds.error}
            />,
            t('FTDs'),
            onOpenSignups,
          )}
        </StatsRow>
        <ChartCol>
          {loading ? (
            <Skeleton block height={CHART_HEIGHT} radius={12} />
          ) : dayView ? (
            <DayBars
              series={dayBars}
              view={interval === 'TODAY' ? 'today' : 'yesterday'}
              height={CHART_HEIGHT}
              formatValue={countFmt}
            />
          ) : (
            <LineChart
              series={series}
              backdrop={
                visitorsPoints
                  ? { name: visitorsName, color: HOME_SERIES_COLORS.visitors, points: visitorsPoints }
                  : undefined
              }
              xLabels={data.xLabels}
              pointLabels={data.pointLabels}
              height={CHART_HEIGHT}
              yTicks={3}
              animate
            />
          )}
        </ChartCol>
      </Inner>
      <LegendWrap>
        <Legend
          items={[
            ...((dayView ? visitorsDay : visitorsPoints)
              ? [{ name: visitorsName, color: HOME_SERIES_COLORS.visitors }]
              : []),
            { name: t('Registrations'), color: HOME_SERIES_COLORS.registrations },
            { name: t('FTDs'), color: HOME_SERIES_COLORS.ftds },
          ]}
        />
      </LegendWrap>
    </MetricFrame>
  );
};

export default AcquisitionMetric;
